import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatError } from "@/lib/api";
import { MEMBER } from "@/constants/testIds";
import { Plus, Trash2, X } from "lucide-react";
import Confirm from "@/components/Confirm";
import { toast } from "@/components/Toaster";

export default function MembersTab({ roomId, isAdmin }) {
  const [members, setMembers] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [form, setForm] = useState({ email: "", role: "user", group: "Default", template_id: "" });
  const [err, setErr] = useState("");
  const [inviteResult, setInviteResult] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [rootFolders, setRootFolders] = useState([]);
  const [scopeMode, setScopeMode] = useState("all"); // 'all' | 'pick'
  const [pickedFolders, setPickedFolders] = useState(new Set());
  const [confirmState, setConfirmState] = useState(null);

  // Pre-partition the template list once per change so the JSX renders
  // the two optgroups without doing three `.filter` passes on every keystroke.
  const { sysTemplates, customTemplates } = useMemo(() => ({
    sysTemplates:    templates.filter((t) => t.system),
    customTemplates: templates.filter((t) => !t.system),
  }), [templates]);

  // `load` is wrapped in useCallback so its identity stays stable across
  // renders — that lets us add it to effect dep arrays without spinning
  // the effect on every render.
  const load = useCallback(async () => {
    const { data } = await api.get(`/rooms/${roomId}/members`);
    setMembers(data);
  }, [roomId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!showInvite || !isAdmin) return;
    api.get("/permission-templates").then(({ data }) => {
      const sys = (data.system || []).map((t) => ({ ...t, system: true }));
      const cust = (data.custom || []).map((t) => ({ ...t, system: false }));
      setTemplates([...sys, ...cust]);
    }).catch(() => setTemplates([]));
    // Load root folders for the optional folder-scope picker.
    api.get(`/rooms/${roomId}/documents`).then(({ data }) => {
      setRootFolders((data || []).filter((d) => d.type === "folder" && !d.parent_id));
    }).catch(() => setRootFolders([]));
  }, [showInvite, isAdmin, roomId]);

  const invite = async (e) => {
    e.preventDefault(); setErr(""); setInviteResult(null);
    try {
      const payload = {
        email: form.email,
        role: form.role,
        group: form.group,
        ...(form.template_id ? { template_id: form.template_id } : {}),
        ...(scopeMode === "pick" ? { allowed_folder_ids: Array.from(pickedFolders) } : {}),
      };
      const { data } = await api.post(`/rooms/${roomId}/members`, payload);
      const link = `${window.location.origin}/accept-invite?token=${data.invite_link}`;
      setInviteResult({ ...data, link, email: form.email });
      setForm({ email: "", role: "user", group: "Default", template_id: "" });
      setScopeMode("all");
      setPickedFolders(new Set());
      await load();
    } catch (e) { setErr(formatError(e)); }
  };

  const remove = (m) => {
    setConfirmState({
      title: "Remove member?",
      message: `${m.email} will lose access to this room immediately. They can be re-invited later.`,
      confirmLabel: "Remove member",
      destructive: true,
      onConfirm: async () => {
        try {
          await api.delete(`/rooms/${roomId}/members/${m.user_id}`);
          await load();
          toast.success(`Removed ${m.email}`);
        } catch (e) {
          toast.error(formatError(e));
          throw e;
        }
      },
    });
  };

  const closeInvite = () => {
    setShowInvite(false);
    setInviteResult(null);
    setErr("");
    setForm({ email: "", role: "user", group: "Default", template_id: "" });
    setScopeMode("all");
    setPickedFolders(new Set());
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="label-mono text-muted-foreground">— {members.length} member{members.length !== 1 && "s"}</div>
        {isAdmin && (
          <button
            data-testid={MEMBER.inviteBtn}
            onClick={() => setShowInvite(true)}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition"
          >
            <Plus className="w-4 h-4" /> Invite member
          </button>
        )}
      </div>
      <div className="border border-border">
        <div className="grid grid-cols-[1fr_1fr_120px_120px_80px] label-mono text-muted-foreground border-b border-border bg-secondary">
          <div className="px-4 py-3">Email</div>
          <div className="px-4 py-3">Name</div>
          <div className="px-4 py-3">Role</div>
          <div className="px-4 py-3">Group</div>
          <div className="px-4 py-3"></div>
        </div>
        {members.map((m) => (
          <div key={m.user_id} className="grid grid-cols-[1fr_1fr_120px_120px_80px] items-center border-b border-border last:border-b-0">
            <div className="px-4 py-3 text-sm">{m.email}</div>
            <div className="px-4 py-3 text-sm text-muted-foreground">{m.name}</div>
            <div className="px-4 py-3 mono text-xs uppercase tracking-wider">{m.role}</div>
            <div className="px-4 py-3 mono text-xs">{m.group}</div>
            <div className="px-4 py-3 text-right">
              {isAdmin && (
                <button data-testid={MEMBER.removeBtn} onClick={() => remove(m)} className="p-2 rounded-md hover:bg-destructive hover:text-destructive-foreground transition">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        ))}
        {members.length === 0 && <div className="px-4 py-12 text-center text-sm text-muted-foreground">No members yet.</div>}
      </div>

      {showInvite && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6" onClick={closeInvite}>
          <div className="bg-card border border-border rounded-md w-full max-w-md p-6 space-y-4 relative" onClick={(e) => e.stopPropagation()}>
            <button
              data-testid="member-invite-close"
              onClick={closeInvite}
              aria-label="Close invite dialog"
              className="absolute top-3 right-3 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition"
            >
              <X className="w-4 h-4" />
            </button>
            {!inviteResult ? (
              <form className="space-y-4" onSubmit={invite}>
                <h3 className="font-display text-xl tracking-tight">Invite member</h3>
                <input
                  data-testid={MEMBER.inviteEmail}
                  required type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="email@company.com"
                  className="w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <div className="grid grid-cols-2 gap-3">
                  <select
                    data-testid={MEMBER.inviteRole}
                    value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                    className="px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="user">User (limited)</option>
                    <option value="custom">Custom (limited mgmt)</option>
                    <option value="admin">Administrator</option>
                  </select>
                  <input
                    data-testid={MEMBER.inviteGroup}
                    value={form.group} onChange={(e) => setForm({ ...form, group: e.target.value })}
                    placeholder="Group" className="px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <div>
                  <label className="label-mono">Permission template (optional)</label>
                  <select
                    data-testid={MEMBER.inviteTemplate}
                    value={form.template_id}
                    onChange={(e) => setForm({ ...form, template_id: e.target.value })}
                    className="mt-1 w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="">— No template (room defaults)</option>
                    {sysTemplates.length > 0 && (
                      <optgroup label="System">
                        {sysTemplates.map((t) => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </optgroup>
                    )}
                    {customTemplates.length > 0 && (
                      <optgroup label="Custom">
                        {customTemplates.map((t) => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                  {form.template_id && (
                    <p className="mt-1 mono text-xs text-muted-foreground">
                      {templates.find((t) => t.id === form.template_id)?.description || "Permissions will be applied on invite."}
                    </p>
                  )}
                </div>

                <div>
                  <label className="label-mono">Folder access</label>
                  <div className="mt-1 grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      data-testid="invite-scope-all"
                      onClick={() => setScopeMode("all")}
                      className={`px-3 py-2 rounded-md text-sm border transition ${scopeMode === "all" ? "border-primary bg-primary/10 text-foreground" : "border-border hover:bg-secondary"}`}
                    >All folders</button>
                    <button
                      type="button"
                      data-testid="invite-scope-pick"
                      onClick={() => setScopeMode("pick")}
                      className={`px-3 py-2 rounded-md text-sm border transition ${scopeMode === "pick" ? "border-primary bg-primary/10 text-foreground" : "border-border hover:bg-secondary"}`}
                    >Pick specific folders</button>
                  </div>
                  {scopeMode === "pick" && (
                    <div className="mt-3 border border-border rounded-md max-h-56 overflow-y-auto divide-y divide-border">
                      {rootFolders.length === 0 ? (
                        <div className="px-4 py-6 text-xs text-muted-foreground text-center">
                          No root folders in this room yet.
                        </div>
                      ) : rootFolders.map((f) => (
                        <label key={f.id} className="flex items-center gap-3 px-3 py-2 hover:bg-secondary transition cursor-pointer">
                          <input
                            data-testid={`invite-folder-${f.id}`}
                            type="checkbox"
                            checked={pickedFolders.has(f.id)}
                            onChange={() => {
                              setPickedFolders((prev) => {
                                const next = new Set(prev);
                                if (next.has(f.id)) next.delete(f.id);
                                else next.add(f.id);
                                return next;
                              });
                            }}
                            className="accent-primary"
                          />
                          <div className="min-w-0 flex-1 text-sm truncate">{f.name}</div>
                          {f.restricted && (
                            <span className="mono text-[10px] uppercase tracking-wider text-purple-500">restricted</span>
                          )}
                        </label>
                      ))}
                    </div>
                  )}
                  {scopeMode === "pick" && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      Un-picked root folders will be hidden from this user. Existing members keep their access.
                    </p>
                  )}
                </div>
                {err && <div className="text-sm text-destructive">{err}</div>}
                <button data-testid={MEMBER.inviteSubmit} type="submit" className="w-full px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90">Send invite</button>
              </form>
            ) : (
              <div className="space-y-4">
                <h3 className="font-display text-xl tracking-tight">Invite sent</h3>
                <div className="text-sm">
                  Invited <span className="font-mono">{inviteResult.email}</span>.
                </div>
                {inviteResult.template_applied && (
                  <div className="text-xs p-3 rounded-md bg-secondary border border-border" data-testid="member-invite-template-applied">
                    <span className="label-mono text-muted-foreground">— Template applied · </span>
                    <span className="font-medium">{inviteResult.template_applied.name}</span>
                  </div>
                )}
                {inviteResult.folder_scope_applied && (
                  <div className="text-xs p-3 rounded-md bg-secondary border border-border" data-testid="member-invite-scope-applied">
                    <span className="label-mono text-muted-foreground">— Folder scope · </span>
                    <span className="font-medium">{inviteResult.folder_scope_applied.folder_ids.length} folder(s)</span>
                  </div>
                )}
                <div className={`text-xs p-3 rounded-md ${inviteResult.email_sent ? "bg-accent/10 text-accent-foreground border border-accent/30" : "bg-destructive/10 text-destructive border border-destructive/30"}`}>
                  {inviteResult.email_sent
                    ? "✓ Email delivered via Resend"
                    : `Email could not be sent${inviteResult.email_error ? ` (${inviteResult.email_error})` : ""}. Share the link below manually.`}
                </div>
                <div>
                  <label className="label-mono">Invite link</label>
                  <div className="mt-1 flex gap-2">
                    <input
                      readOnly value={inviteResult.link}
                      className="flex-1 px-3 py-2 bg-background border border-border rounded-md text-xs mono"
                    />
                    <button
                      data-testid="member-invite-copy"
                      onClick={() => { navigator.clipboard.writeText(inviteResult.link); }}
                      className="px-3 py-2 rounded-md border border-border hover:bg-secondary transition text-xs"
                    >Copy</button>
                  </div>
                  <div className="mono text-[10px] text-muted-foreground mt-2">Link expires in 7 days.</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setInviteResult(null)} className="flex-1 px-4 py-2 rounded-md border border-border hover:bg-secondary transition">Invite another</button>
                  <button onClick={closeInvite} className="flex-1 px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition">Done</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <Confirm state={confirmState} onClose={() => setConfirmState(null)} />
    </div>
  );
}
